import { css } from '@emotion/react';
import Caption from '@polestar/component-warehouse-react/components/caption/Caption';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import styled from '@emotion/styled';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, Fragment, useCallback, useState } from 'react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import WithTooltip from '@polestar/component-warehouse-react/components/with-tooltip/WithTooltip';
import { overflowEllipsis } from '@polestar/component-warehouse-react/helpers/style';
import MinimalHitboxTypes from '@polestar/component-warehouse-react/tokens/minimal-hitbox-types/MinimalHitboxTypes';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const FormField = forwardRef(({ id, prefixId, label, trailing, hint, errorMessage, error, forId, tooltip, tooltipPosition = Orientations.vertical, focused = false, disabled = false, required = false, minHeight = true, isTextInput = true, children, onMouseEnter, onMouseLeave, border = true, innerRef, ...props }, ref) => {
    const hasError = hasErrorValue(error, errorMessage);
    const errorText = getErrorText(error, errorMessage);
    const identifier = prefixId || forId;
    const [hover, setHover] = useState(false);
    const stateProps = {
        error: hasError,
        hover,
        disabled,
        focused: focused,
    };
    const memoizedId = useSSRSafeId('form-field', identifier);
    const memoizedLabelId = `${memoizedId}-label`;
    const memoizedMessageId = `${memoizedId}-message`;
    const captionErrorProps = hasError && errorText !== undefined
        ? {
            error: true,
            role: 'alert',
        }
        : {};
    const handleMouseEnter = useCallback((event) => {
        setHover(true);
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(event);
    }, [onMouseEnter]);
    const handleMouseLeave = useCallback((event) => {
        setHover(false);
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(event);
    }, [onMouseLeave]);
    return (React.createElement(Container, { ref: ref, id: id, ...props, disabled: disabled },
        React.createElement(StateContainer, { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
            label && (React.createElement(StyledLabel, { as: props['data-type'] === 'legend' ? 'legend' : 'label', htmlFor: forId, id: memoizedLabelId, tooltip: !!tooltip && tooltip !== '', ...stateProps },
                label,
                React.createElement("span", { "aria-hidden": true }, required ? '\u00A0*' : ''))),
            React.createElement(InputContainer, { isTextInput: isTextInput, minHeight: minHeight, ...stateProps, ref: innerRef },
                children,
                trailing && (React.createElement(TrailingContainer, { disabled: disabled }, typeof trailing === 'object' && 'glyph' in trailing ? (React.createElement(Icon, { icon: trailing })) : (trailing))),
                tooltip && tooltip !== '' && (React.createElement(FormFieldTooltipContainer, { label: tooltip, ...stateProps, orientation: tooltipPosition, minimalHitboxType: MinimalHitboxTypes.both })))),
        border && React.createElement(Border, { ...stateProps, "data-id": "border" }),
        !!((hasError && errorText !== undefined) || hint) && (React.createElement(Fragment, null,
            React.createElement(Spacer, { spacing: Spacings.xSmall, orientation: Orientations.vertical }),
            React.createElement(Caption, { id: memoizedMessageId, ...captionErrorProps, disabled: disabled }, errorText || hint),
            React.createElement(Spacer, { spacing: Spacings.xxSmall, orientation: Orientations.vertical })))));
});
FormField.displayName = 'FormField';
export default FormField;
const Container = styled.div(({ disabled }) => css(css `
      display: flex;
      flex-direction: column;
    `, !disabled &&
    css `
        pointer-events: auto;
      `, disabled &&
    css `
        user-select: none;
        pointer-events: none;
      `));
const StateContainer = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const TrailingContainer = styled('div', shouldNotForwardProps(true, 'disabled'))(({ disabled, theme }) => css(css `
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-inline-end: ${Spacings.medium};
    `, disabled &&
    css `
        color: ${theme.formField.states.disabled.iconColor};
      `));
const StyledLabel = styled(Label, shouldNotForwardProps(false, 'disabled', 'focused', 'hover'))(({ hover, error, disabled, theme, focused, tooltip }) => css(css `
        color: ${theme.formField.states.enabled.labelColor};
        white-space: nowrap;
        max-width: 100%;
        transition: ${createCSSTransition('color', MotionEasings.default, MotionDurations.fast)};
        ${overflowEllipsis};
        /* add spacing below the label */
        margin-block-end: ${Spacings.xSmall};
        &:after {
          content: '\\00a0';
        }
      `, tooltip &&
    css `
          padding-inline-end: ${createCustomSpacing(
    /* 10 is the spacing, 16 is the size of the icon */
    26 / 16)};
        `, focused &&
    css `
          color: ${theme.formField.states.focused.labelColor};
        `, hover &&
    css `
          color: ${theme.formField.states.hover.labelColor};
        `, error &&
    css `
          color: ${theme.formField.states.error.labelColor};
        `, disabled &&
    css `
          cursor: default;
          color: ${theme.formField.states.disabled.labelColor};
        `));
/**
 * @internal
 */
export const FormFieldTooltipContainer = forwardRef(({ orientation, label, minimalHitboxType, ...props }, ref) => {
    return (React.createElement(TooltipContainer, { ref: ref, orientation: orientation },
        React.createElement(WithTooltip, { label: label, ...props, minimalHitboxType: minimalHitboxType },
            React.createElement(StyledIcon, { icon: Icons.questionCircle, size: Sizes.small }))));
});
const TooltipContainer = styled('div', shouldNotForwardProps(false, 'orientation'))(({ orientation }) => css(css `
      pointer-events: all;
      & span {
        position: relative;
      }
    `, orientation === Orientations.vertical &&
    css `
        position: absolute;
        inset-inline-end: 0;
        inset-block-start: 0;
        z-index: 1;
        & span::before {
          inset-block-start: 10%;
        }
      `, orientation === Orientations.horizontal &&
    css `
        padding-inline-start: ${createCustomSpacing(10 / 16)};
        display: inline-flex;
        align-self: flex-start;
        padding-block-start: ${createCustomSpacing(13 / 16)};
        & span::before {
          inset-block-start: 40%;
        }
      `));
const StyledIcon = styled(Icon)(({ theme }) => css `
    color: ${theme.formField.states.enabled.tooltipIconColor};
  `);
const InputContainer = styled.div(({ theme, isTextInput, minHeight }) => css(css `
      display: flex;
      flex: 1;
      flex-direction: row;
      align-items: center;
      -webkit-tap-highlight-color: transparent;

      /* ensure that icon is of limited height but not the input */
      min-height: ${minHeight ? Spacings.xxLarge : 0};

      /* prevents overflow, even if trailing/leading has unknown width https://stackoverflow.com/a/60633311 */
      min-width: 100%;
      max-width: 100%;
    `, isTextInput &&
    css `
        background-color: ${theme.formField.backgroundColor};
      `));
const Border = styled.div(({ theme, error, hover, disabled, focused }) => css(css `
      overflow: hidden;
      height: 1px;
      background-color: ${theme.formField.states.enabled.borderColor};
      transition: ${createCSSTransition('background-color', MotionEasings.default, MotionDurations.xSlow)};
    `, hover &&
    css `
        background-color: ${theme.formField.states.hover.borderColor};
      `, focused &&
    css `
        background-color: ${theme.formField.states.focused.borderColor};
        height: 2px;
        transform: translateY(-1px);
        margin-block-end: -1px;
      `, error &&
    css `
        background-color: ${theme.formField.states.error.borderColor};
      `, error &&
    focused &&
    css `
        background-color: ${theme.formField.states.focused.borderColor};
        height: 2px;
        transform: translateY(-1px);
        margin-block-end: -1px;
      `, disabled &&
    css `
        background-color: ${theme.formField.states.disabled.borderColor};
      `));
/**
 * @internal
 */
export const FormFieldIconButton = forwardRef((props, ref) => (React.createElement(StyledIconButton, { ref: ref, variant: IconButtonVariants.transparent, ...props })));
FormFieldIconButton.displayName = 'FormFieldIconButton';
const StyledIconButton = styled(IconButton) `
  color: ${({ disabled, theme }) => theme.formField.states[disabled ? 'disabled' : 'enabled'].iconColor};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;
export const FormFieldPlaceholder = styled.span(({ theme }) => css `
    color: ${theme.formField.states.enabled.placeholderColor};
  `);
/**
 * to ensure that text input fields have always the same height this is exported to be used in the components
 * and be able to change it at one place for all components
 *
 * @internal
 */
export const minimalFormInputFieldHeight = css `
  min-height: ${Spacings.xxLarge};
`;
/**
 * @internal
 */
export const hasErrorValue = (error, errorMessage) => (typeof error === 'string' && error !== '') || !!error || !!errorMessage;
/**
 * @internal
 */
export const getErrorText = (error, errorMessage) => (typeof error === 'string' || typeof error === 'object') && error !== null
    ? error
    : (typeof errorMessage === 'string' || typeof errorMessage === 'object') &&
        errorMessage !== null
        ? errorMessage
        : undefined;
